"use client";

import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Avatar,
  IconButton,
  Alert,
  Stack,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ChevronRight } from "lucide-react";
import { useTwinspaceContext } from "./Layout";
import { useEffect, useState } from "react";
import { IRoomPublish, ITwinSpace, IUserAvatar, IUserRoom } from "@/api";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import avatar from "../../images/avatar.png";
import { useDialog } from "@/hooks/use-dialog";
import ConfirmDialog, {
  ConfirmDialogButtonType,
} from "@/components/ConfirmDialog";
import { useAlert } from "@/contexts/AlertContext";

const getDescription = (inserted_at: any) => {
  const timestamp = new Date(inserted_at).getTime();
  const current_time = new Date().getTime();
  const time_difference = current_time - timestamp;

  if (time_difference < 86400000) {
    return "Created Today";
  } else if (time_difference < 172800000) {
    return "Created Yesterday";
  } else if (time_difference < 432000000) {
    return "Created Recently";
  } else if (time_difference < 604800000) {
    return "Created 5 days ago";
  } else if (time_difference < 1209600000) {
    return "Created a week ago";
  } else {
    return "Created long time ago";
  }
};

export default function Twinspace() {
  const { removeRoom } = useTwinspaceContext();
  const [percent, setPercent] = useState<string>("0");
  const [totalUserRoom, setTotalUserRoom] = useState<number>(0);
  const [maxRoom, setMaxRoom] = useState<number>(0);
  const [userAvatar, setUserAvatar] = useState<IUserAvatar | []>([]);
  const [listRoomPublish, setListRoomPublish] = useState<IRoomPublish | []>([]);
  const [listUserRoom, setListUserRoom] = useState<IUserRoom | []>([]);
  const [pageSize, setPageSize] = useState(4);
  const confirmDialog = useDialog();
  const alert = useAlert();

  const [totalPageAvatars, setTotalPageAvatars] = useState(0);
  const [currentPageAvatar, setCurrentPageAvatar] = useState(1);
  const [currentAvatar, setCurrentAvatar] = useState<IUserAvatar["data"]>([]);

  const [totalPageUserRooms, setTotalPageUserRooms] = useState(0);
  const [currentPageUserRooms, setCurrentPageUserRooms] = useState(1);
  const [currentUserRoom, setCurrentUserRoom] = useState<IUserRoom["data"]>([]);

  const [totalPagePublishRooms, setTotalPagePublishRooms] = useState(0);
  const [currentPagePublishRooms, setCurrentPagePublishRooms] = useState(1);
  const [currentPublishRoom, setCurrentPublishRoom] = useState<
    IRoomPublish["data"]
  >([]);
  const [loading, setLoading] = useState(false);
  const [twinspace, setTwinspace] = useState<ITwinSpace>({} as any);
  const { loadDataMyTwinspace } = useTwinspaceContext();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchTwinspace = async () => {
      setLoading(true); // Bắt đầu tải dữ liệu
      try {
        const res = await loadDataMyTwinspace();
        setTwinspace(res);
      } catch (error) {
        console.error("Error fetching twinspace:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTwinspace();
  }, []);
  useEffect(() => {
    if (twinspace) {
      setPercent(twinspace.percent);
      if (twinspace.user_room && twinspace.user_room.total_rows) {
        setTotalUserRoom(twinspace.user_room.total_rows);
      }
      if (twinspace.user_room) {
        setListUserRoom(twinspace.user_room);
        setTotalPageUserRooms(
          Math.ceil(Number(twinspace.user_room.total_rows) / pageSize)
        );
      }
      if (twinspace.user_avatar) {
        setUserAvatar(twinspace.user_avatar);
        setTotalPageAvatars(
          Math.ceil(Number(twinspace.user_avatar.total_rows) / pageSize)
        );
      }
      if (twinspace.list_room_publish) {
        setListRoomPublish(twinspace.list_room_publish);
        setTotalPagePublishRooms(
          Math.ceil(Number(twinspace.list_room_publish.total_rows) / pageSize)
        );
      }
      setMaxRoom(twinspace.max_room);
    }
  }, [twinspace]);

  useEffect(() => {
    setPageSize((prevSize) => (isMobile ? 1 : 4));
  }, [isMobile]);

  useEffect(() => {
    // const newSize = isMobile ? 1 : 4;
    // if (pageSize !== newSize) {
    //     setPageSize(newSize);
    //     return; // Tránh gọi fetch khi pageSize vừa thay đổi
    // }
    if (Array.isArray(userAvatar)) {
      setCurrentAvatar([]);
    } else {
      const currentAvatar = userAvatar.data.slice(
        (currentPageAvatar - 1) * pageSize,
        currentPageAvatar * pageSize
      );
      setCurrentAvatar(currentAvatar);
    }
  }, [userAvatar, currentPageAvatar, isMobile, pageSize]);

  const handleNextPageUserAvatar = () => {
    if (currentPageAvatar < totalPageAvatars) {
      setCurrentPageAvatar(currentPageAvatar + 1);
    }
  };

  const handlePrevPageUserAvatar = () => {
    if (currentPageAvatar > 1) {
      setCurrentPageAvatar(currentPageAvatar - 1);
    }
  };

  useEffect(() => {
    // const newSize = isMobile ? 1 : 4;
    // if (pageSize !== newSize) {
    //     setPageSize(newSize);
    //     return; // Tránh gọi fetch khi pageSize vừa thay đổi
    // }
    if (Array.isArray(listUserRoom)) {
      setCurrentUserRoom([]);
    } else {
      const currentUserRooms = listUserRoom.data.slice(
        (currentPageUserRooms - 1) * pageSize,
        currentPageUserRooms * pageSize
      );
      setCurrentUserRoom(currentUserRooms);
    }
  }, [listUserRoom, currentPageUserRooms, isMobile, pageSize]);

  const handleNextPageUserRoom = () => {
    if (currentPageUserRooms < totalPageUserRooms) {
      setCurrentPageUserRooms(currentPageUserRooms + 1);
    }
  };

  const handlePrevPageUserRoom = () => {
    if (currentPageUserRooms > 1) {
      setCurrentPageUserRooms(currentPageUserRooms - 1);
    }
  };

  const handleRemoveRoom = (room_id: string) => {
    confirmDialog.show({
      title: "Remove Room",
      content: "Are you sure you want to remove this room?",
      buttons: [
        {
          buttonType: ConfirmDialogButtonType.YES,
          onClick: () => {
            // alert.showWait();
            removeRoom(room_id)
              .then(() => {
                // alert.success('Schools added to partner successfully');
                // navigate(0);
                setListUserRoom((prev) =>
                  Array.isArray(prev)
                    ? prev // Nếu là mảng rỗng, không làm gì
                    : {
                        ...prev,
                        data: prev.data.filter(
                          (room: any) => room.hub_sid !== room_id
                        ),
                      }
                );
                setCurrentUserRoom((prev) =>
                  prev.filter((room: any) => room.hub_sid !== room_id)
                );
              })
              .catch((err) => {
                alert.error("Failed to remove Room");
              })
              .finally(() => {
                // alert.hideWait();
              });
          },
        },
        {
          buttonType: ConfirmDialogButtonType.NO,
          onClick: () => {
            confirmDialog.hide();
          },
        },
      ],
    });
  };

  useEffect(() => {
    // const newSize = isMobile ? 1 : 4;
    // if (pageSize !== newSize) {
    //     setPageSize(newSize);
    //     return; // Tránh gọi fetch khi pageSize vừa thay đổi
    // }
    if (Array.isArray(listRoomPublish)) {
      setCurrentPublishRoom([]);
    } else {
      const currentPublishRooms = listRoomPublish.data.slice(
        (currentPagePublishRooms - 1) * pageSize,
        currentPagePublishRooms * pageSize
      );
      setCurrentPublishRoom(currentPublishRooms);
    }
  }, [listRoomPublish, currentPagePublishRooms, isMobile, pageSize]);

  const handleNextPagePublisRoom = () => {
    if (currentPagePublishRooms < totalPagePublishRooms) {
      setCurrentPagePublishRooms(currentPagePublishRooms + 1);
    }
  };

  const handlePrevPagePublisRoom = () => {
    if (currentPagePublishRooms > 1) {
      setCurrentPagePublishRooms(currentPagePublishRooms - 1);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "white", borderRadius: 2, boxShadow: 1 }}>
      <ConfirmDialog {...confirmDialog} />
      <Typography
        variant="h6"
        sx={{
          color: "rgb(15, 105, 185)",
          fontWeight: 500,
        }}
      >
        Twinspace
      </Typography>
      {loading ? (
        <Box
          sx={{
            p: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Header Alert */}
          <Alert
            severity="info"
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#e0e0e0",
            }}
            action={
              <Button
                color="success"
                variant="contained"
                size="small"
                sx={{
                  bgcolor: "#4caf50",
                  textTransform: "none",
                }}
                disabled
              >
                Upgrade package
              </Button>
            }
          >
            You Have Used Up ({percent}%) Total Storage And ({totalUserRoom}/
            {maxRoom}) Rooms
          </Alert>

          {/* My Avatar Section */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h6">My Avatar</Typography>
              <a
                href={
                  Array.isArray(listUserRoom) || listUserRoom.data.length === 0
                    ? `${process.env.DOMAIN_PHUB}/${process.env.ROOM_ID}/media/media/avatars?filter=my-avatars`
                    : `${process.env.DOMAIN_PHUB}/${listUserRoom.data[0].hub_sid}/media/media/avatars?filter=my-avatars`
                }
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <Button
                  color="success"
                  variant="contained"
                  size="small"
                  sx={{
                    bgcolor: "#4caf50",
                    textTransform: "none",
                  }}
                >
                  Manage avatar
                </Button>
              </a>
            </Box>

            <Box sx={{ position: "relative" }}>
              {Array.isArray(userAvatar) ||
              (userAvatar && userAvatar.data.length === 0) ? (
                <Typography
                  sx={{
                    color: "rgb(102, 102, 102)",
                    fontSize: "1rem",
                    textAlign: "center",
                  }}
                >
                  No data available
                </Typography>
              ) : (
                <>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      overflowX: "hidden",
                      pb: 2,
                    }}
                  >
                    {currentAvatar.map((avatar: any) => (
                      <Card
                        key={avatar.avatar_sid}
                        sx={{
                          flex: isMobile ? "1 1 100%" : "1 1 calc(25% - 16px)", // Chiều rộng mỗi thẻ (25% trừ khoảng cách spacing)
                          maxWidth: isMobile ? "100%" : "calc(25% - 16px)", // Đảm bảo thẻ không vượt quá chiều rộng container
                          textAlign: "center",
                          boxShadow: "none",
                          border: "1px solid #e0e0e0",
                          marginBottom: 2,
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          image={avatar.thumbnail_url}
                          alt="Avatar"
                          sx={{
                            width: "110px",
                            margin: "0 auto",
                          }}
                        />
                        <CardContent>
                          <Typography variant="body2">{avatar.name}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  <IconButton
                    onClick={handlePrevPageUserAvatar} // Chuyển trang về trước
                    sx={{
                      position: "absolute",
                      left: -16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      "&:hover": {
                        bgcolor: "rgb(247, 250, 255)",
                      },
                    }}
                  >
                    <ArrowBackIos />
                  </IconButton>

                  <IconButton
                    onClick={handleNextPageUserAvatar} // Chuyển trang tới tiếp theo
                    sx={{
                      position: "absolute",
                      right: -16,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      "&:hover": {
                        bgcolor: "rgb(247, 250, 255)",
                      },
                    }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>

          {/* My Rooms Section */}
          <Box sx={{ mb: 4, position: "relative" }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h6">My rooms</Typography>
              <Box>
                <a
                  href={process.env.DOMAIN_PHUB + "/createroom"}
                  target="_blank"
                  style={{ textDecoration: "none" }}
                >
                  <Button variant="outlined" size="small" sx={{ mr: 1 }}>
                    Create Room
                  </Button>
                </a>
                <a
                  href={process.env.DOMAIN_PHUB + "/spoke"}
                  target="_blank"
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: "rgb(255, 90, 31)",
                      "&:hover": {
                        bgcolor: "rgb(230, 81, 28)",
                      },
                    }}
                  >
                    Scene Editor
                  </Button>
                </a>
              </Box>
            </Box>
            {Array.isArray(listUserRoom) ||
            (listUserRoom && listUserRoom.data.length === 0) ? (
              <Typography
                sx={{
                  color: "rgb(102, 102, 102)",
                  fontSize: "1rem",
                  textAlign: "center",
                }}
              >
                No data available
              </Typography>
            ) : (
              <>
                <Grid container spacing={2}>
                  {currentUserRoom.map((room) => (
                    <Grid item xs={12} sm={6} md={3} key={room.hub_sid}>
                      <Card
                        sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            borderBottom: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <img
                              src={avatar}
                              alt="PAvatar Icon"
                              width="24"
                              height="24"
                              style={{ borderRadius: "100%" }}
                            />
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {room.visit_count} visits
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {room.max_occupant_count} visitor
                            </Typography>
                          </Box>
                          <Button
                            onClick={() => {
                              handleRemoveRoom(room.hub_sid);
                            }}
                            variant="contained"
                            size="small"
                            color="warning"
                            sx={{
                              bgcolor: "rgb(255, 90, 31)",
                              "&:hover": {
                                bgcolor: "rgb(230, 81, 28)",
                              },
                            }}
                          >
                            Remove
                          </Button>
                        </Box>
                        <a
                          href={process.env.DOMAIN_PHUB + "/" + room.hub_sid}
                          target="_blank"
                        >
                          <CardMedia
                            component="img"
                            height="200"
                            image={
                              room.thumbnail_url &&
                              room.thumbnail_url.includes("/files/") &&
                              room.thumbnail_url.split("/files/")[1]
                                ? room.thumbnail_url
                                : `${process.env.DOMAIN_PHUB}/app-thumbnail.png`
                            }
                            alt={room.name}
                          />
                        </a>
                        <CardContent>
                          <Typography gutterBottom variant="subtitle1">
                            {room.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getDescription(room.inserted_at)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <IconButton
                  onClick={handlePrevPageUserRoom} // Chuyển trang về trước
                  sx={{
                    position: "absolute",
                    left: -16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "rgb(247, 250, 255)",
                    },
                  }}
                >
                  <ArrowBackIos />
                </IconButton>

                <IconButton
                  onClick={handleNextPageUserRoom} // Chuyển trang tới tiếp theo
                  sx={{
                    position: "absolute",
                    right: -16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "rgb(247, 250, 255)",
                    },
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>
              </>
            )}
          </Box>

          {/* New Rooms Section */}
          <Box sx={{ position: "relative" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              New rooms by community
            </Typography>
            {Array.isArray(listRoomPublish) ||
            (listRoomPublish && listRoomPublish.data.length === 0) ? (
              <Typography
                sx={{
                  color: "rgb(102, 102, 102)",
                  fontSize: "1rem",
                  textAlign: "center",
                }}
              >
                No data available
              </Typography>
            ) : (
              <>
                <Grid container spacing={2}>
                  {currentPublishRoom.map((room) => (
                    <Grid item xs={12} sm={6} md={3} key={room.hub_sid}>
                      <Card
                        sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            borderBottom: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <img
                              src={avatar}
                              alt="PAvatar Icon"
                              width="24"
                              height="24"
                              style={{ borderRadius: "100%" }}
                            />
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {room.visit_count} visits
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {room.max_occupant_count} visitor
                            </Typography>
                          </Box>
                        </Box>
                        <a
                          href={process.env.DOMAIN_PHUB + "/" + room.hub_sid}
                          target="_blank"
                        >
                          <CardMedia
                            component="img"
                            height="200"
                            image={
                              room.thumbnail_url &&
                              room.thumbnail_url.includes("/files/") &&
                              room.thumbnail_url.split("/files/")[1]
                                ? room.thumbnail_url
                                : `${process.env.DOMAIN_PHUB}/app-thumbnail.png`
                            }
                            alt={room.name}
                          />
                        </a>
                        <CardContent>
                          <Typography gutterBottom variant="subtitle1">
                            {room.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getDescription(room.inserted_at)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <IconButton
                  onClick={handlePrevPagePublisRoom} // Chuyển trang về trước
                  sx={{
                    position: "absolute",
                    left: -16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "rgb(247, 250, 255)",
                    },
                  }}
                >
                  <ArrowBackIos />
                </IconButton>

                <IconButton
                  onClick={handleNextPagePublisRoom} // Chuyển trang tới tiếp theo
                  sx={{
                    position: "absolute",
                    right: -16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    bgcolor: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: "rgb(247, 250, 255)",
                    },
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>
              </>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
